import { cn } from "@workspace/web-ui/lib/utils";
import { PatientAvatar } from "./patient-avatar";
import { PatientInfo } from "./patient-info";
import { PatientStatus } from "./patient-status";
import { Patient } from "../../../api/types";
import { Image } from "@workspace/web-ui/components/image";

interface PatientCardProps {
    patient: Patient;
    className?: string;
    selected: Patient | null;
    onSelect: (patient: Patient | null) => void;
    program?: string; // Optional program prop to handle different programs
}

export const PatientCard = ({ patient, selected, className, onSelect, program }: PatientCardProps) => {
    const isSelected = selected?.userId === patient.userId;

    const handleClick = () => {
        if (isSelected) {
            onSelect(null);
        } else {
            onSelect(patient);
        }
    };

    return (
        <div
            className={cn(
                "bg-white rounded-lg sm:rounded-xl p-3 sm:p-4 flex flex-col sm:flex-row gap-3 sm:gap-4 items-start justify-between border shadow-sm cursor-pointer transition-all duration-200 hover:shadow-md hover:scale-[1.02]",
                "cursor-pointer w-full",
                isSelected && "border-[1.5px] border-[#7F7D83]",
                className
            )}
            onClick={handleClick}
            role="button"
            tabIndex={0}
            aria-pressed={isSelected}
        >
            <div className="flex flex-row gap-3 sm:gap-4 items-start sm:items-center w-full">
                <PatientAvatar initials={patient.initials} />
                <div className="w-full flex flex-col space-y-1">
                    <div className="flex gap-2 items-center justify-between w-full">
                    <div className="flex gap-2 items-center">
                        <h2 className="text-base font-semibold">{patient.name}</h2>
                        {program === "urgent-care" ? (
                            patient.urgent_care_onboarded && patient.identity_verified && (
                                <div className="">
                                    <Image src="/icons/check.svg" alt="timeline" layout="constrained" className="w-4 h-4 sm:w-5 sm:h-5" width={96} height={96} />
                                </div>
                            )
                        ) : (
                            patient.medicalPlan?.payment_status === "paid" && patient.glp1_guide_accepted && patient.identity_verified && patient.supplementary_health_assessment && Object.keys(patient.supplementary_health_assessment).length > 0 && (
                                <div className="">
                                    <Image src="/icons/check.svg" alt="timeline" layout="constrained" className="w-4 h-4 sm:w-5 sm:h-5" width={96} height={96} />
                                </div>
                            )
                        )}
                    </div>
                                {program === "urgent-care" && patient.isNew && (
                <div className="bg-[#4A85FF] rounded-[6666px] text-white text-[14px] w-[51px] text-center">
                    New
                </div>
            )}
                    </div>
                    <PatientInfo patient={patient} program={program ?? ""} />
                    <div className="flex gap-2">
                        {program === "urgent-care" ? (
                            patient?.patient_sessions_state === "all_sessions_ended" ? (
                                <PatientStatus status={"Closed"} />
                            ) :
                                patient?.patient_sessions_state === "pending_review" ? (
                                    <PatientStatus status={"Pending Review"} />
                                ) :
                                    patient?.patient_sessions_state === "in_progress" ? (
                                        <PatientStatus status={"In Progress"} />
                                    ) : null
                        ) : (
                            patient?.medicalPlan?.payment_status === "paid" ? (
                                <PatientStatus status={"Paid"} />
                            ) : (
                                <PatientStatus status={"Not Paid"} />
                            )
                        )}
                        {program !== "urgent-care" && patient.isNew && (
                            <PatientStatus status={"New"} />
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};